import { AuthStorageBuilder } from './authStorage.service';

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  expiresAt: number;
}

export interface UserClaims {
  idToken: string;
  email: string;
  username: string;
  groups: string[];
  userId: string;
  userRole: 'OWNER' | 'TECHNICIAN' | 'AIRSHIP_ADMIN';
  userPermissions: (
    | 'canEditMaterialPrice'
    | 'canEditMaterialCharge'
    | 'canEditEquipmentPrice'
    | 'canEditServicePrice'
  )[];
  organizationId: string;
}

export type UserManagerCache = {
  tokens: AuthTokens;
  claims: UserClaims;
};

export interface AuthCacheManagerBuilder {
  get(): Promise<Partial<UserManagerCache> & { isExpired: boolean }>;
  set(tokens: AuthTokens): Promise<void>;
  clear(): Promise<void>;
  startEventListener(): void;
  dispose(): void;
}

interface AuthCacheManagerOptions {
  storageKey: string;
  userCache: Map<string, UserManagerCache>;
  nowProvider: () => number;
}

const DEFAULT_OPTIONS: AuthCacheManagerOptions = {
  storageKey: 'airship-auth',
  userCache: new Map<string, UserManagerCache>(),
  nowProvider: () => Date.now(),
};

export class AuthCacheManagerService implements AuthCacheManagerBuilder {
  constructor(
    private authStorage: AuthStorageBuilder<AuthTokens>,
    private options = DEFAULT_OPTIONS
  ) {}

  private get userCache() {
    return this.options.userCache;
  }

  private get storageKey() {
    return this.options.storageKey;
  }

  private get nowProvider() {
    return this.options.nowProvider;
  }

  async get() {
    const cachedUser = this.userCache.get(this.storageKey);

    if (!cachedUser) {
      const tokens = await this.authStorage.get(this.storageKey);

      if (!tokens) {
        return { isExpired: true };
      }

      const isExpired = this.isTokenExpired(tokens.expiresAt);

      const claims = this.mapClaims(tokens);
      this.userCache.set(this.storageKey, { tokens, claims });

      return { tokens, claims, isExpired };
    }

    return {
      ...cachedUser,
      isExpired: this.isTokenExpired(cachedUser.tokens.expiresAt),
    };
  }

  async set(tokens: AuthTokens) {
    const claims = this.mapClaims(tokens);

    const wrappedEntry = {
      ...tokens,
      expiresAt: this.getNowSeconds() + tokens.expiresIn,
    };

    this.userCache.set(this.storageKey, { tokens: wrappedEntry, claims });
    await this.authStorage.set(this.storageKey, wrappedEntry);
  }

  async clear() {
    this.userCache.delete(this.storageKey);
    await this.authStorage.remove(this.storageKey);
  }

  startEventListener() {
    this.authStorage.addEventListener(this.storageEventListener.bind(this));
  }

  dispose() {
    this.authStorage.removeEventListener();
  }

  private isTokenExpired(expiresAt: number) {
    const nowSeconds = this.getNowSeconds();
    const DEFAULT_OFFSET = 60;

    return expiresAt - DEFAULT_OFFSET < nowSeconds;
  }

  private mapClaims(tokens: AuthTokens) {
    const userClaims = JSON.parse(atob(tokens.idToken.split('.')[1]));
    return {
      idToken: tokens.idToken,
      email: userClaims['email'],
      username: userClaims['cognito:username'],
      groups: userClaims['cognito:groups'],
      userId: userClaims['custom:userId'],
      userRole: userClaims['custom:userRole'],
      userPermissions: (userClaims['custom:userPermissions'] || '')
        .split(',')
        .filter(Boolean),
      organizationId: userClaims['custom:organizationId'],
    };
  }

  private getNowSeconds() {
    return Math.floor(this.nowProvider() / 1000);
  }

  private storageEventListener(key: string, tokens: AuthTokens | null) {
    if (key === this.storageKey) {
      if (tokens === null) {
        this.userCache.delete(key);
      }

      if (tokens) {
        const claims = this.mapClaims(tokens);
        this.userCache.set(key, { tokens, claims });
      }
    }
  }
}
